import Tabs from "./tabs";
import { useEffect, useState, useRef } from "react";
import { Box, Avatar, Stack, Typography, Chip, Dialog, DialogContent } from "@mui/material";
import DataTableWithCheckbox from "../../../components/dataTableWithSearch/DataTableWithCheckBox";
import { useSelector, useDispatch } from "react-redux";
import DownloadIcon from '@mui/icons-material/Download';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import InsightsIcon from '@mui/icons-material/Insights';
import GroupsIcon from '@mui/icons-material/Groups';
import * as XLSX from 'xlsx';
import SnackbarMessage from "../../../components/snackBar/snackBar";
import FilterMultiSelect from "../../../components/selections/filterMultiSelect";
import { formatDate } from "../../../components/formatDateAndTime/formatDateAndTime";
import ViewQrCodeCounts from "./ViewQrCodeCounts";
import ViewQrCodeModal from "./viewQrCode";
import IndividualData from "./individualData";
import { getQRIndividualThunk } from "../qrCodeThunk";
import { QR, QrActionButton, QrModalHeader, qrDialogPaperSx } from "./qrUi";
import { QrCreateForm } from "./qrCodeCreation";

const fmt = (n) => new Intl.NumberFormat("en-IN").format(Number(n) || 0);

export default function QRCodeList() {

    const dispatch = useDispatch();

    const { qrCodes, status, error } = useSelector((state) => state.qrCode);

    const [selectedRows, setSelectedRows] = useState([]);
    const [openCountsModal, setOpenCountsModal] = useState(false);
    const [openViewQrModal, setOpenViewQrModal] = useState(false);
    const [openindividualData, setOpenindividualData] = useState(false)
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [individualRange, setIndividualRange] = useState(""); // period label for the Individual Data popup
    const [statFilter, setStatFilter] = useState(null); // count key from a clicked stat card, or null = all
    const [rowData, setRowData] = useState([]);
    const [selectedBrachDeptValue, setSelectBranchDeptValue] = useState([]);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // Clickable count chip: opens the individual-data modal for that date range (same behaviour as before)
    const countCell = (row, key, dateRangeId, color) => {
        const value = row.original[key];
        if (!(value > 0)) {
            return <Typography variant="body2" color="text.disabled">{value}</Typography>;
        }
        return (
            <Box
                component="span"
                title="View contacts"
                onClick={() => {
                    setRowData(row.original);
                    setOpenindividualData(true);
                    setIndividualRange({ "1": "Today", "2": "Yesterday", "3": "Last 7 days", "4": "Last 30 days", "0": "All time" }[dateRangeId] || "");
                    dispatch(getQRIndividualThunk({
                        qr_code_id: row.original.id,
                        date_range_id: dateRangeId
                    }));
                }}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: 34,
                    px: 1,
                    py: 0.25,
                    borderRadius: 5,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 700,
                    color,
                    bgcolor: `${color}14`,
                    border: `1px solid ${color}33`,
                    transition: "all .15s ease",
                    "&:hover": { bgcolor: color, color: "#fff", borderColor: color },
                }}
            >
                {value}
            </Box>
        );
    };

    // Columns with clickable QR Name
    const columns = [
        { header: "SNO", accessorKey: "sno", enableGlobalFilter: true },
        {
            accessorKey: "title",
            header: "Name",
            Cell: ({ row }) => (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.25}
                    title="View QR code"
                    onClick={() => {
                        setRowData(row.original);
                        setOpenViewQrModal(true);
                    }}
                    sx={{
                        cursor: "pointer",
                        // clickable: always blue + underlined (darker on hover)
                        "& .qr-name": { color: "#2563eb !important", textDecoration: "underline", textDecorationColor: "#2563eb66", textUnderlineOffset: "3px" },
                        "&:hover .qr-name": { color: "#1d4ed8 !important", textDecorationColor: "#1d4ed8" },
                        "&:hover .qr-thumb": { bgcolor: QR.main, color: "#fff" },
                    }}
                >
                    <Avatar
                        className="qr-thumb"
                        variant="rounded"
                        sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: `${QR.main}14`, color: QR.main, transition: "all .15s ease" }}
                    >
                        <QrCode2Icon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography className="qr-name" variant="body2" fontWeight={700} color="#0f172a">
                        {row.original.title}
                    </Typography>
                </Stack>
            )
        },
        {
            accessorKey: "url",
            header: "Key",
            Cell: ({ row }) =>
                row.original.url ? (
                    <Box
                        component="span"
                        sx={{
                            px: 1,
                            py: 0.25,
                            borderRadius: 1.5,
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: "#334155",
                            bgcolor: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                        }}
                    >
                        {row.original.url}
                    </Box>
                ) : "-",
        },
        { accessorKey: "branch_department_name", header: "Branch Department" },
        {
            accessorKey: "tag_name",
            header: "Tag",
            Cell: ({ row }) =>
                row.original.tag_name ? (
                    <Chip
                        size="small"
                        label={row.original.tag_name}
                        sx={{ fontWeight: 600, color: QR.purple, bgcolor: `${QR.purple}12`, border: `1px solid ${QR.purple}26` }}
                    />
                ) : "-",
        },
        { accessorKey: "today_count", header: "Today", Cell: ({ row }) => countCell(row, "today_count", "1", QR.main) },
        { accessorKey: "yesterday_count", header: "Yesterday", Cell: ({ row }) => countCell(row, "yesterday_count", "2", QR.blue) },
        { accessorKey: "last7days_count", header: "Last 7 days", Cell: ({ row }) => countCell(row, "last7days_count", "3", QR.purple) },
        { accessorKey: "last30days_count", header: "Last 30 days", Cell: ({ row }) => countCell(row, "last30days_count", "4", QR.amber) },
        { accessorKey: "overall_count", header: "Over All", Cell: ({ row }) => countCell(row, "overall_count", "0", "#0f172a") },
        {
            accessorKey: "inserted_time",
            header: "Created Date",
            Cell: ({ row }) => (
                <Typography variant="body2" color="text.secondary">
                    {formatDate(row.original.inserted_time)}
                </Typography>
            ),
        }
    ];

    // Filter options for branch/department
    const branchDeptOptions = qrCodes
        .filter(item => item.branch_department_id != null && item.branch_department_name != null)
        .map((item) => ({ value: item.branch_department_id, label: item.branch_department_name }))
        .filter((obj, index, self) => index === self.findIndex((o) => o.value === obj.value));

    useEffect(() => {
        setSelectBranchDeptValue(branchDeptOptions);
    }, [qrCodes]);


    const handleMultiSelectChange = (_name, selectedValues) => {
        setSelectBranchDeptValue(selectedValues);
    };

    const getValue = (val, fallback = "-") => val !== null && val !== undefined && val !== "" ? val : fallback;

    const handleQrExcelDownload = () => {
        const formattedData = selectedRows.map((item) => ({
            "KeyWord": getValue(item.url),
            "Name": getValue(item.title),
            "Department Name": getValue(item.department_name),
            "Branch Name": getValue(item.branch_name),
            "Tag Name": getValue(item.tag_name),
            "Today": getValue(item.today_count),
            "Yesterday": getValue(item.yesterday_count),
            "Last7days": getValue(item.last7days_count),
            "Last30days": getValue(item.last30days_count),
            "OverAll": getValue(item.overall_count),
            "Inserted Time":
                item.inserted_time ? formatDate(item.inserted_time) : "-"
        }));

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
        XLSX.writeFile(workbook, 'qrReports.xlsx');
    };



    const renderQrFilter = () => {
        return (
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2 }}>
                <Box sx={{ width: { xs: "100%", sm: 260 } }}>
                    <FilterMultiSelect
                        name="branchDept"
                        label="Branch Department"
                        options={branchDeptOptions}
                        value={selectedBrachDeptValue || []}
                        onChange={handleMultiSelectChange}
                    />
                </Box>

                {/* Actions for selected rows (same conditions/handlers as before), aligned right */}
                {+selectedRows.length > 0 && (
                    <Box sx={{ ml: { sm: "auto" } }}>
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                            <QrActionButton
                                icon={<DownloadIcon sx={{ fontSize: 17 }} />}
                                label="Download Excel"
                                color="#d97706" // orange
                                count={selectedRows.length}
                                onClick={handleQrExcelDownload}
                            />
                            {+selectedRows.length > 1 && (
                                <QrActionButton
                                    variant="solid"
                                    color={QR.main} // green
                                    gradientTo={QR.light}
                                    icon={<CompareArrowsIcon sx={{ fontSize: 17 }} />}
                                    label="Compare QR Codes"
                                    count={selectedRows.length}
                                    onClick={() => setOpenCountsModal(true)}
                                />
                            )}
                        </Stack>
                    </Box>
                )}
            </Box>
        )
    }



    // Snackbar for copy or error
    const isFirstStatus = useRef(true);
    useEffect(() => {
        if (isFirstStatus.current) {
            isFirstStatus.current = false;
            return;
        }
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);

    // Summary stats shown inside the header card (display only)
    const sum = (key) => qrCodes.reduce((s, q) => s + (Number(q[key]) || 0), 0);
    // filterKey: clicking a card shows only QR codes with leads in that period (null = all QR codes)
    const stats = [
        { label: "QR Codes", value: qrCodes.length, icon: <QrCode2Icon />, color: QR.main, filterKey: null },
        { label: "Leads Today", value: sum("today_count"), icon: <TodayIcon />, color: QR.blue, filterKey: "today_count" },
        { label: "Last 7 Days", value: sum("last7days_count"), icon: <DateRangeIcon />, color: QR.purple, filterKey: "last7days_count" },
        { label: "Last 30 Days", value: sum("last30days_count"), icon: <InsightsIcon />, color: QR.amber, filterKey: "last30days_count" },
        { label: "Overall Leads", value: sum("overall_count"), icon: <GroupsIcon />, color: "#0f172a", filterKey: "overall_count" },
    ].map((st) => ({
        ...st,
        active: st.filterKey === statFilter,
        // click: apply this filter; click the active card again (or "QR Codes") to show all
        onClick: () => setStatFilter((cur) => (st.filterKey === null || cur === st.filterKey ? null : st.filterKey)),
    }));

    // Table rows: branch filter + stat-card filter
    const tableData = qrCodes
        .filter((item) => selectedBrachDeptValue.map((v) => +v.value).includes(+item.branch_department_id))
        .filter((item) => !statFilter || (Number(item[statFilter]) || 0) > 0);

    return (
        <div>
            {/* Header card: title, stats and the table all inside one card */}
            <Tabs stats={stats.map((st) => ({ ...st, value: fmt(st.value) }))} onCreate={() => setOpenCreateModal(true)}>
                {/* Toolbar + table inside the header card */}
                <DataTableWithCheckbox
                    columns={columns}
                    data={tableData}
                    emptyMessage={
                        {
                            today_count: "No QR codes received leads today.",
                            last7days_count: "No QR codes received leads in the last 7 days.",
                            last30days_count: "No QR codes received leads in the last 30 days.",
                            overall_count: "No QR codes have captured any leads yet.",
                        }[statFilter] || "No QR codes found for the selected branch."
                    }
                    selectionResetKey={`${statFilter}|${selectedBrachDeptValue.map((v) => v.value).join(",")}`}
                    enableRowSelection={true}
                    onSelectionChange={setSelectedRows}
                    renderFilters={renderQrFilter()}
                    status={status}
                    searchFirst
                />
            </Tabs>

            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />

            <ViewQrCodeModal
                show={openViewQrModal}
                handleClose={() => setOpenViewQrModal(false)}
                rowData={rowData}
            />

            <ViewQrCodeCounts
                show={openCountsModal}
                handleClose={() => setOpenCountsModal(false)}
                rowData={selectedRows}
            />
            {/* Create QR popup */}
            <Dialog
                open={openCreateModal}
                onClose={() => setOpenCreateModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: 640 } }}
            >
                <QrModalHeader
                    icon={<QrCode2Icon />}
                    title="Create QR Code"
                    subtitle="Generate a WhatsApp QR code and start capturing leads"
                    onClose={() => setOpenCreateModal(false)}
                />
                <DialogContent sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 3, md: 4 }, pb: { xs: 2.5, md: 3.5 } }}>
                    {openCreateModal && (
                        <QrCreateForm
                            embedded
                            onCancel={() => setOpenCreateModal(false)}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <IndividualData
                show={openindividualData}
                handleClose={() => setOpenindividualData(false)}
                qrTitle={rowData?.title}
                rangeLabel={individualRange}
            // rowData={selectedRows}
            />
        </div>
    );
}
